import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AdminToolUsage } from "@/components/AdminToolUsage";

const STATUS = { published: "text-[#5EE6D0]", scheduled: "text-[#F3DDAE]", planned: "text-muted-foreground", unpublished: "text-destructive" };

const VolumeCell = ({ row, onSaved }) => {
  const [v, setV] = useState(row.volume || "");
  useEffect(() => setV(row.volume || ""), [row.volume]);
  const save = async () => {
    if (v === row.volume) return;
    try { onSaved(await api.patch(`/admin/seo/ledger/${row.id}`, { volume: v }).then((r) => r.data)); toast.success(`${row.id} volume saved.`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return <Input value={v} onChange={(e) => setV(e.target.value)} onBlur={save} aria-label={`Google volume for ${row.primary_keyword}`}
    data-testid={`ledger-volume-${row.id}`} className="h-9 w-28 rounded-lg bg-background/60 text-xs" />;
};

const AddKeyword = ({ onAdded }) => {
  const [kw, setKw] = useState("");
  const [type, setType] = useState("tags");
  const add = async (e) => {
    e.preventDefault();
    try { onAdded((await api.post("/admin/seo/ledger", { primary_keyword: kw, type })).data); setKw(""); toast.success("Keyword added."); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={add} className="flex flex-wrap gap-2" data-testid="ledger-add-form">
      <Input value={kw} onChange={(e) => setKw(e.target.value)} placeholder="New keyword, e.g. etsy tags for lanyards" data-testid="ledger-add-input" className="h-10 w-72 rounded-full bg-background/60" />
      <select value={type} onChange={(e) => setType(e.target.value)} data-testid="ledger-add-type" className="h-10 rounded-full border border-white/10 bg-background/60 px-3 text-sm">
        <option value="tags">Tag page</option><option value="guide">Guide</option>
      </select>
      <Button type="submit" variant="outline" data-testid="ledger-add-submit" className="h-10 rounded-full">Add keyword</Button>
    </form>
  );
};

const DifficultyCell = ({ row, onSaved }) => {
  const save = async (difficulty) => {
    try { onSaved((await api.patch(`/admin/seo/ledger/${row.id}`, { difficulty })).data); toast.success(`${row.id} difficulty saved.`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <select value={row.difficulty || "TBV"} onChange={(e) => save(e.target.value)} data-testid={`ledger-difficulty-${row.id}`} aria-label={`Difficulty for ${row.primary_keyword}`}
      className="h-9 rounded-lg border border-white/10 bg-background/60 px-2 text-xs">
      {["Easy", "Med", "Hard", "TBV"].map((d) => <option key={d}>{d}</option>)}
    </select>
  );
};

const FIELDS = [["title", "Title (≤60)"], ["meta_description", "Meta description (≤155)"], ["h1", "H1"], ["intro", "Intro (first 100 words)"]];

const ContentEditor = ({ slug, onClose, onSaved }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setD(null);
    if (slug) api.get(`/admin/content/${slug}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [slug]);
  const act = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); onSaved(r.data); setD((x) => ({ ...x, row: r.data })); toast.success(msg); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = () => act(() => api.patch(`/admin/content/${slug}`, Object.fromEntries(FIELDS.map(([k]) => [k, d[k]]))), "Saved. Admin edits are never overwritten by seed files.");
  return (
    <Dialog open={!!slug} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="content-editor">
        <DialogHeader><DialogTitle>{d ? d.h1 : "Loading…"}</DialogTitle></DialogHeader>
        {d && (
          <div className="space-y-4 text-sm">
            <p className="text-muted-foreground">Status: <span data-testid="content-editor-status" className="text-foreground">{d.row.status}</span> · {d.row.publish_date?.slice(0, 16).replace("T", " ")} UTC</p>
            {FIELDS.map(([k, l]) => (
              <label key={k} className="block space-y-1">
                <span className="text-xs text-muted-foreground">{l} · {(d[k] || "").length} chars</span>
                <Textarea value={d[k] || ""} onChange={(e) => setD({ ...d, [k]: e.target.value })} data-testid={`content-editor-${k}`} rows={k === "intro" ? 5 : 2} />
              </label>
            ))}
            <details className="rounded-xl border border-white/10 p-3"><summary className="cursor-pointer">Preview body ({d.sections.length} sections)</summary>
              {d.sections.map((s) => <div key={s.h2} className="mt-3"><p className="font-semibold">{s.h2}</p>{s.blocks.slice(0, 2).map((b, i) => <p key={i} className="mt-1 text-muted-foreground">{b.text || (b.items || b.tags || []).join(" · ")}</p>)}</div>)}
            </details>
            <div className="flex flex-wrap gap-2">
              <Button onClick={save} disabled={busy} data-testid="content-editor-save" className="btn-aurora rounded-full">Save edits</Button>
              {d.row.status === "unpublished"
                ? <Button variant="outline" disabled={busy} onClick={() => act(() => api.post(`/admin/content/${slug}/publish`), "Republished.")} data-testid="content-editor-publish" className="rounded-full">Republish</Button>
                : <Button variant="outline" disabled={busy} onClick={() => act(() => api.post(`/admin/content/${slug}/unpublish`), "Unpublished. The page now returns 404.")} data-testid="content-editor-unpublish" className="rounded-full">Unpublish</Button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const AdminSeoLedger = () => {
  const [rows, setRows] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [f, setF] = useState({ wave: "all", type: "all", status: "all", q: "" });
  const [edit, setEdit] = useState(null);
  const file = useRef(null);
  const load = () => api.get("/admin/seo/ledger").then((r) => setRows(r.data.rows)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const upsert = (r) => setRows((rs) => (rs.some((x) => x.id === r.id) ? rs.map((x) => (x.id === r.id ? r : x)) : [...rs, r]));
  const remove = async (id) => {
    try { await api.delete(`/admin/seo/ledger/${id}`); setRows((rs) => rs.filter((x) => x.id !== id)); toast.success(`${id} deleted.`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const importCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/seo/import-csv", fd);
      toast.success(`Imported: ${data.matched.length} matched, ${data.new_ideas.length} new ideas.`);
      setIdeas(data.new_ideas);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    e.target.value = "";
  };
  const download = async () => {
    const { data } = await api.get("/admin/seo/ledger.csv", { responseType: "blob" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(data), download: "keyword_ledger.csv" });
    a.click();
  };
  if (!rows) return <div className="h-64 rounded-2xl shimmer" />;
  const counts = rows.reduce((c, r) => ({ ...c, [r.status]: (c[r.status] || 0) + 1 }), {});
  const q = f.q.trim().toLowerCase();
  const shown = rows.filter((r) => (f.wave === "all" || String(r.wave) === f.wave) && (f.type === "all" || r.type === f.type)
    && (f.status === "all" || r.status === f.status) && (!q || `${r.id} ${r.primary_keyword} ${r.slug}`.toLowerCase().includes(q)));
  const sel = (k, opts) => (
    <select value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`ledger-filter-${k}`} aria-label={`Filter by ${k}`}
      className="h-10 rounded-full border border-white/10 bg-background/60 px-3 text-sm">
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  return (
    <div data-testid="admin-seo-ledger" className="space-y-5">
      <AdminToolUsage />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p data-testid="ledger-counts" className="text-sm text-muted-foreground">
          {rows.length} keywords · {counts.published || 0} published · {counts.scheduled || 0} scheduled · {counts.planned || 0} planned{counts.unpublished ? ` · ${counts.unpublished} unpublished` : ""}
        </p>
        <div className="flex flex-wrap gap-2">
          <input ref={file} type="file" accept=".csv,.tsv,text/csv" hidden onChange={importCsv} data-testid="ledger-import-input" />
          <Button variant="outline" onClick={() => file.current?.click()} data-testid="ledger-import-button" className="h-10 rounded-full">Import Keyword Planner CSV</Button>
          <Button variant="outline" onClick={download} data-testid="ledger-download-button" className="h-10 rounded-full">Download CSV</Button>
        </div>
      </div>
      <AddKeyword onAdded={upsert} />
      <div className="flex flex-wrap gap-2" data-testid="ledger-filters">
        {sel("wave", [["all", "All waves"], ["1", "Wave 1"], ["2", "Wave 2"], ["seasonal", "Seasonal"]])}
        {sel("type", [["all", "All types"], ["guide", "Guides"], ["tags", "Tag pages"], ["tool", "Tools"], ["page", "Pages"]])}
        {sel("status", [["all", "All statuses"], ["published", "Published"], ["scheduled", "Scheduled"], ["planned", "Planned"], ["unpublished", "Unpublished"]])}
        <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search keyword or slug" data-testid="ledger-search" className="h-10 w-64 rounded-full bg-background/60" />
      </div>
      {ideas.length > 0 && (
        <div data-testid="ledger-new-ideas" className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm">
          <p className="font-semibold">New ideas from your CSV (not yet in the ledger)</p>
          <ul className="mt-2 flex flex-wrap gap-2">{ideas.slice(0, 60).map((i) => <li key={i.keyword} className="rounded-full border border-white/10 px-3 py-1 text-xs">{i.keyword} · {i.volume || "—"}</li>)}</ul>
        </div>
      )}
      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full text-left text-xs" data-testid="ledger-table">
          <thead className="bg-white/[0.04] text-muted-foreground">
            <tr>{["ID", "Wave", "Type", "Primary keyword", "Secondary", "Slug", "Volume (Google)", "Difficulty", "Status", "Publish date", "URL", ""].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} data-testid={`ledger-row-${r.id}`} className="border-t border-white/[0.06]">
                <td className="px-3 py-2 font-mono">{r.id}</td>
                <td className="px-3 py-2">{r.wave}</td>
                <td className="px-3 py-2">{{ guide: "Guide", tags: "Tags", tool: "Tool", page: "Page" }[r.type] || r.type}</td>
                <td className="px-3 py-2 text-foreground">{r.primary_keyword}</td>
                <td className="max-w-[220px] px-3 py-2 text-muted-foreground"><span className="line-clamp-2" title={r.secondary_keywords.join(", ")}>{r.secondary_keywords.join(", ") || "—"}</span></td>
                <td className="px-3 py-2 font-mono">{r.slug}</td>
                <td className="px-3 py-2"><VolumeCell row={r} onSaved={upsert} /></td>
                <td className="px-3 py-2"><DifficultyCell row={r} onSaved={upsert} /></td>
                <td className={`px-3 py-2 font-medium ${STATUS[r.status]}`} data-testid={`ledger-status-${r.id}`}>{r.status}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.publish_date ? r.publish_date.slice(0, 16).replace("T", " ") + " UTC" : "—"}</td>
                <td className="px-3 py-2">{r.url && r.status === "published" ? <a href={r.url.replace("https://aethertags.com", "")} target="_blank" rel="noreferrer" className="text-[#F3DDAE] underline underline-offset-4">Open ↗</a> : r.url ? <span className="text-muted-foreground">{r.url.replace("https://aethertags.com", "")}</span> : "—"}</td>
                <td className="px-3 py-2">
                  {r.url && <Button variant="outline" size="sm" onClick={() => setEdit(r.slug)} data-testid={`ledger-edit-${r.id}`} className="h-8 rounded-full text-xs">Preview / edit</Button>}
                  {!r.url && r.wave !== 1 && <Button variant="outline" size="sm" onClick={() => remove(r.id)} data-testid={`ledger-delete-${r.id}`} className="h-8 rounded-full text-xs">Delete</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ContentEditor slug={edit} onClose={() => setEdit(null)} onSaved={(r) => { upsert(r); }} />
    </div>
  );
};
